'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';

import { Form, FormField } from '@eliadmin/core/src/components/ui/form';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { Button } from '../ui/button';
import { CheckboxField, InputField, SelectField, TextareaField } from '../ui/input-field';

/**
 * Formulár v sekcii Kontakt — Figma `46:1699`.
 *
 * Napojenie kopíruje `components/contact/contact-form.tsx` z jadra: zod schéma
 * cez `useFormValidationMessages`, `useForm` + `zodResolver`, odoslanie cez
 * `useMutation(sendContactMessage)` na `/store/forms/contact`, reCAPTCHA v3,
 * meranie cez `trackFormSubmitSuccess`, hlásenia cez `toast` a rozpad 422 do
 * polí cez `ValidationError`.
 *
 * Schému zadal David; polia `custom.text4130`–`text4134` sú v poradí, v akom
 * stoja vo formulári: typ prepravy, odkiaľ, kam, rozmery, váha. Jediná úprava
 * oproti zadaniu je `custom: z.object({...})` namiesto obyčajného objektu —
 * zod inak schému odmietne (viď hlásenie).
 */

const inquirySchema = (VM: FormValidationMessages) =>
  z.object({
    custom: z.object({
      text4130: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      text4131: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      text4132: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      text4133: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      text4134: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    }),
    phone: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    text: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': z.string().default(''),
  });

type InquiryFormInput = z.input<ReturnType<typeof inquirySchema>>;
type InquiryFormValues = z.output<ReturnType<typeof inquirySchema>>;

/** TODO(CMS): nahradiť zoznamom z modulu Služby. */
const TRANSPORT_TYPES = [
  { value: 'Expresná preprava', label: 'Expresná preprava' },
  { value: 'Pozemná preprava', label: 'Pozemná preprava' },
  { value: 'Logistické služby', label: 'Logistické služby' },
  { value: 'Monitoring a špedícia', label: 'Monitoring a špedícia' },
];

/** TODO: skutočná stránka o ochrane osobných údajov, keď vznikne. */
const PRIVACY_HREF = '/ochrana-osobnych-udajov';

export function ContactQuoteForm() {
  const { t } = useTranslations();
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<InquiryFormInput, any, InquiryFormValues>({
    resolver: zodResolver(inquirySchema(formValidationMessages)),
    defaultValues: {
      custom: { text4130: '', text4131: '', text4132: '', text4133: '', text4134: '' },
      phone: '',
      email: '',
      text: '',
      gdpr: false,
      'g-recaptcha-response': '',
    },
  });

  const { mutateAsync } = useMutation({ mutationFn: sendContactMessage });

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await mutateAsync({ data: { ...data,  }, locale });

      trackFormSubmitSuccess({
        form_id: 'contact-inquiry',
        form_name: 'contact_inquiry_form',
        form_destination: typeof window !== 'undefined' ? window.location.href : undefined,
        form_length: Object.keys(data).length,
        send_to: generalLayout.marketing?.gtm_id || '',
      });

      toast({ title: t('text658'), variant: 'success' });
      form.reset();
      router.push(ROUTES.THANK_YOU());
    } catch (err) {
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }
      toast({ title: t('text657'), description: (err as any)?.message, variant: 'destructive' });
    }
  });

  return (
    <Form {...form}>
        <CaptchaFormMessage errors={form.formState.errors} />
      <form
        onSubmit={handleSubmit}
        className={cn(
          'ea-contact-quote-form',
          'flex min-w-0 flex-1 flex-col gap-8 rounded-16 bg-[#FFFFFF] p-5',
          'md:p-6 lg:p-8',
        )}
      >
        <div className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="custom.text4130"
            render={({ field, fieldState }) => (
              <SelectField
                {...field}
                label="Typ prepravy"
                placeholder="Vyberte typ prepravy"
                options={TRANSPORT_TYPES}
                required
                error={fieldState.error?.message}
              />
            )}
          />

          <FieldRow>
            <FormField
              control={form.control}
              name="custom.text4131"
              render={({ field, fieldState }) => (
                <InputField
                  {...field}
                  label="Odkiaľ"
                  placeholder="Mesto, krajina"
                  required
                  error={fieldState.error?.message}
                />
              )}
            />
            <FormField
              control={form.control}
              name="custom.text4132"
              render={({ field, fieldState }) => (
                <InputField
                  {...field}
                  label="Kam"
                  placeholder="Mesto, krajina"
                  required
                  error={fieldState.error?.message}
                />
              )}
            />
          </FieldRow>

          <FieldRow>
            <FormField
              control={form.control}
              name="custom.text4133"
              render={({ field, fieldState }) => (
                <InputField
                  {...field}
                  label="Rozmery (cm)"
                  placeholder="napr. 120 × 80 × 100"
                  required
                  error={fieldState.error?.message}
                />
              )}
            />
            <FormField
              control={form.control}
              name="custom.text4134"
              render={({ field, fieldState }) => (
                <InputField
                  {...field}
                  label="Váha (kg)"
                  placeholder="napr. 450"
                  required
                  error={fieldState.error?.message}
                />
              )}
            />
          </FieldRow>

          <FieldRow>
            <FormField
              control={form.control}
              name="phone"
              render={({ field, fieldState }) => (
                <InputField
                  {...field}
                  label="Telefón"
                  type="tel"
                  placeholder="+421 …"
                  required
                  error={fieldState.error?.message}
                />
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <InputField
                  {...field}
                  label="E-mail"
                  type="email"
                  placeholder="vas@email.sk"
                  required
                  error={fieldState.error?.message}
                />
              )}
            />
          </FieldRow>

          <FormField
            control={form.control}
            name="text"
            render={({ field, fieldState }) => (
              <TextareaField
                {...field}
                label="Obsah správy"
                placeholder="Doplňte termín, druh tovaru alebo špeciálne požiadavky…"
                required
                error={fieldState.error?.message}
              />
            )}
          />

          <FormField
            control={form.control}
            name="gdpr"
            render={({ field, fieldState }) => (
              <CheckboxField
                name={field.name}
                checked={field.value}
                onCheckedChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
              >
                Súhlasím so spracovaním osobných údajov pre účely vypracovania cenovej ponuky. Viac
                informácii{' '}
                <Link
                  href={PRIVACY_HREF}
                  className="text-primary underline-offset-2 hover:underline"
                >
                  nájdete tu
                </Link>
                .
              </CheckboxField>
            )}
          />
        </div>

        {/* Figma: na mobile je najprv tlačidlo a pod ním poznámka, na desktope
            poznámka vľavo a tlačidlo vpravo. */}
        <div
          className={cn(
            'flex flex-col gap-8',
            'lg:flex-row-reverse lg:items-center lg:justify-between lg:gap-6',
          )}
        >
          <Button
            type="submit"
            variant="brand-accent"
            size="brand"
            rounded="pill"
            isLoading={form.formState.isSubmitting}
            className="w-full lg:w-auto"
          >
            Odoslať dopyt
          </Button>

          <p className="max-w-[296px] text-[12px] leading-[1.43] tracking-[-0.036em] text-[#55617A] sm:text-[13px] lg:text-[14px]">
            Odpovieme spravidla do 24 hodín. Vaše údaje použijeme výhradne na vypracovanie ponuky.
          </p>
        </div>
      </form>
    </Form>
  );
}

/** Dvojica polí — na mobile pod sebou, od lg vedľa seba s medzerou 24. */
function FieldRow({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">{children}</div>;
}
